'use client';

import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import CustomerApiService, { type PasswordUpdateData } from '@/services/CustomerApiService';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { extractErrorMessage } from '@/lib/customer-panel/error-handler';
import { validatePassword } from '@/lib/customer-panel/form-utils';
import { ErrorMessage, SuccessMessage, FormField, LoadingSpinner } from '@/lib/customer-panel/ui-components';
import { createFieldAriaAttributes } from '@/lib/customer-panel/accessibility';

interface PasswordFormProps {
  translations?: Record<string, string>;
  onSuccess?: () => void | Promise<void>;
}

export default function PasswordForm({ translations = {}, onSuccess }: PasswordFormProps) {
  const t = useCustomerPanelTranslations(translations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<PasswordUpdateData>({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validation
    const validation = validatePassword(formData.new_password, formData.new_password_confirmation);
    if (!validation.isValid) {
      setError(validation.errors[0] || t('auth.password.validation_failed', 'Password validation failed'));
      setLoading(false);
      return;
    }

    try {
      await CustomerApiService.updatePassword(formData);
      setSuccess(true);
      await onSuccess?.();
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((field: keyof PasswordUpdateData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const inputBaseClasses = "border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed w-full";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {error && <ErrorMessage message={error} />}
      {success && <SuccessMessage message={t('customer_panel.common.update_success', 'Successfully updated')} />}

      <div className="space-y-6">
        <FormField
          id="current_password"
          label={t('customer_panel.profile.current_password', 'Current Password')}
          required
          error={null}
        >
          <motion.input
            type="password"
            id="current_password"
            value={formData.current_password}
            onChange={(e) => handleChange('current_password', e.target.value)}
            required
            className={inputBaseClasses}
            autoComplete="current-password"
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('current_password', t('customer_panel.profile.current_password', 'Current Password'), true)}
          />
        </FormField>

        <FormField
          id="new_password"
          label={t('customer_panel.profile.new_password', 'New Password')}
          required
          error={null}
        >
          <div className="flex flex-col gap-2">
            <motion.input
              type="password"
              id="new_password"
              value={formData.new_password}
              onChange={(e) => handleChange('new_password', e.target.value)}
              required
              minLength={8}
              className={inputBaseClasses}
              autoComplete="new-password"
              whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
              whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
              transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
              {...createFieldAriaAttributes('new_password', t('customer_panel.profile.new_password', 'New Password'), true)}
            />
            <p className="text-xs text-gray-500" id="new_password-hint">
              {t('auth.password.min_length_hint', 'Password must be at least 8 characters')}
            </p>
          </div>
        </FormField>

        <FormField
          id="new_password_confirmation"
          label={t('customer_panel.profile.confirm_password', 'Confirm Password')}
          required
          error={null}
        >
          <motion.input
            type="password"
            id="new_password_confirmation"
            value={formData.new_password_confirmation}
            onChange={(e) => handleChange('new_password_confirmation', e.target.value)}
            required
            minLength={8}
            className={inputBaseClasses}
            autoComplete="new-password"
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('new_password_confirmation', t('customer_panel.profile.confirm_password', 'Confirm Password'), true)}
          />
        </FormField>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-alsero-first text-white rounded-md hover:bg-alsero-second focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label={loading ? t('customer_panel.common.loading', 'Loading...') : t('customer_panel.common.save', 'Save')}
        >
          {loading ? (
            <>
              <LoadingSpinner size="sm" className="inline-block mr-2" />
              {t('customer_panel.common.loading', 'Loading...')}
            </>
          ) : (
            t('customer_panel.common.save', 'Save')
          )}
        </button>
      </div>
    </form>
  );
}

